import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined,
    ContentCopyOutlined, CheckOutlined,
    ShareOutlined, DownloadOutlined,
    QrCodeScannerOutlined, SouthWestOutlined,
    NorthEastOutlined, SwapHorizOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    accent:  '#C8B8E8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    blue:    '#007AFF',
    orange:  '#FF9500',
};

const NG_ACCOUNT = '7823456109';
const NG_BANK    = 'NG Credit MFB';
const USER_NAME  = 'Ahmed Bello';
const USER_TAG   = '@ahmedbello';

// 21×21 QR matrix — finder patterns + data modules
const QR: number[] = [
    1,1,1,1,1,1,1, 0,1,1,0,1,0, 0,1,1,1,1,1,1,1,
    1,0,0,0,0,0,1, 0,0,1,0,0,1, 0,1,0,0,0,0,0,1,
    1,0,1,1,1,0,1, 0,1,0,1,0,0, 0,1,0,1,1,1,0,1,
    1,0,1,1,1,0,1, 0,0,0,1,1,0, 0,1,0,1,1,1,0,1,
    1,0,1,1,1,0,1, 0,1,1,0,0,1, 0,1,0,1,1,1,0,1,
    1,0,0,0,0,0,1, 0,0,0,1,0,1, 0,1,0,0,0,0,0,1,
    1,1,1,1,1,1,1, 0,1,0,1,0,1, 0,1,1,1,1,1,1,1,
    0,0,0,0,0,0,0, 0,0,1,0,0,0, 0,0,0,0,0,0,0,0,
    1,1,0,1,1,0,1, 1,0,0,1,0,1, 1,0,1,1,0,1,1,0,
    0,1,0,0,1,1,0, 0,1,0,1,1,0, 1,1,0,0,1,0,0,1,
    1,0,1,1,0,1,1, 0,0,1,0,0,1, 0,1,1,0,1,1,0,0,
    0,1,1,0,0,0,0, 1,1,0,1,0,0, 1,0,0,1,0,1,1,0,
    1,0,0,1,1,0,1, 0,1,1,0,1,1, 0,1,0,0,1,0,0,1,
    0,0,0,0,0,0,0, 0,1,0,0,1,0, 0,0,1,0,1,1,0,0,
    1,1,1,1,1,1,1, 0,0,1,0,0,1, 0,0,0,0,0,0,0,1,
    1,0,0,0,0,0,1, 0,1,0,1,0,0, 1,0,1,0,1,0,1,0,
    1,0,1,1,1,0,1, 0,0,0,1,1,0, 0,1,1,0,0,1,0,1,
    1,0,1,1,1,0,1, 0,1,1,0,0,1, 1,0,0,1,0,0,1,0,
    1,0,1,1,1,0,1, 0,0,1,0,1,0, 0,1,0,1,1,0,0,1,
    1,0,0,0,0,0,1, 0,1,0,0,0,1, 0,0,1,0,0,1,0,0,
    1,1,1,1,1,1,1, 0,1,1,0,1,0, 1,0,1,0,0,0,1,0,
];

const RECENT_RECEIVED = [
    { id: 1, name: 'Emeka Okafor',   amount: 5000,  time: 'Today · 9:30 AM',     avatar: 'EO' },
    { id: 2, name: 'Ngozi Williams', amount: 12000, time: 'Yesterday · 4:15 PM', avatar: 'NW' },
    { id: 3, name: 'GTBank Transfer', amount: 20000, time: 'Mon · 11:00 AM',     avatar: 'GT' },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const QRPage: React.FC = () => {
    const navigate  = useNavigate();
    const theme     = useTheme();
    const isDark    = theme.palette.mode === 'dark';

    const [copiedField, setCopiedField] = useState('');
    const [activeTab,   setActiveTab]   = useState<'receive' | 'scan'>('receive');
    const [reqAmount,   setReqAmount]   = useState('');
    const [reqNote,     setReqNote]     = useState('');

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const copy = (text: string, field: string) => {
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(''), 2000);
    };

    const numAmount = parseFloat(reqAmount) || 0;

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.blue, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate('/wallet')}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            QR Code
                        </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: alpha(B.purple, .2), border: `1px solid ${alpha(B.light, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <QrCodeScannerOutlined sx={{ color: B.light, fontSize: 20 }} />
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Tab switcher ── */}
                <Box sx={{ ...glassCard, p: .75, mb: 2, display: 'flex' }}>
                    {(['receive', 'scan'] as const).map(tab => (
                        <Box key={tab} onClick={() => setActiveTab(tab)} sx={{
                            flex: 1, py: 1.1, borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                            background: activeTab === tab ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : 'transparent',
                            transition: 'all .2s',
                            boxShadow: activeTab === tab ? `0 4px 16px ${alpha(B.purple, .35)}` : 'none',
                        }}>
                            <Typography sx={{ color: activeTab === tab ? '#fff' : textFaint, fontWeight: 700, fontSize: '.82rem', textTransform: 'capitalize' }}>
                                {tab === 'receive' ? '⬇ Receive Money' : '⬆ Scan & Pay'}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* ══ RECEIVE TAB ══ */}
                {activeTab === 'receive' && (
                    <>
                        {/* QR card */}
                        <Box sx={{ ...glassCard, p: 3, mb: 2, textAlign: 'center' }}>

                            {/* User info */}
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.25, mb: 2.5 }}>
                                <Box sx={{ width: 44, height: 44, borderRadius: '50%', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Instrument Serif', serif", fontSize: '1rem', color: '#fff' }}>
                                    AB
                                </Box>
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.9rem', letterSpacing: '-.02em' }}>{USER_NAME}</Typography>
                                    <Typography sx={{ color: textFaint, fontSize: '.7rem' }}>{USER_TAG}</Typography>
                                </Box>
                            </Box>

                            {/* QR code */}
                            <Box sx={{
                                width: 220, height: 220, mx: 'auto', mb: 2.5,
                                borderRadius: '24px', p: '18px',
                                background: '#fff',
                                boxShadow: isDark
                                    ? `0 0 0 1px ${alpha(B.light, .15)}, 0 20px 60px ${alpha(B.dark, .5)}`
                                    : `0 8px 40px ${alpha(B.purple, .18)}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                position: 'relative',
                            }}>
                                {/* QR grid */}
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(21, 1fr)', gap: '1.8px', width: '100%', position: 'relative', zIndex: 1 }}>
                                    {QR.map((cell, i) => (
                                        <Box key={i} sx={{
                                            borderRadius: '1px',
                                            background: cell ? B.deep : 'transparent',
                                            aspectRatio: '1',
                                        }} />
                                    ))}
                                </Box>
                                {/* Center logo */}
                                <Box sx={{
                                    position: 'absolute', width: 40, height: 40, borderRadius: '11px',
                                    background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: `0 4px 16px ${alpha(B.purple, .4)}`,
                                    zIndex: 2,
                                }}>
                                    <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '.75rem', letterSpacing: '-.02em' }}>NG</Typography>
                                </Box>
                            </Box>

                            <Typography sx={{ color: textMid, fontSize: '.78rem', mb: .5 }}>
                                Scan to send money to <strong style={{ color: textPri }}>{USER_NAME}</strong>
                            </Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.7rem', mb: 2 }}>
                                Works with GTBank, Access, UBA, Kuda, Opay & more
                            </Typography>

                            {/* Action buttons */}
                            <Box display="flex" gap={1.25} justifyContent="center">
                                {[
                                    { label: 'Share',    icon: ShareOutlined,    action: () => {} },
                                    { label: 'Download', icon: DownloadOutlined, action: () => {} },
                                ].map(btn => {
                                    const Icon = btn.icon;
                                    return (
                                        <Button key={btn.label} onClick={btn.action} startIcon={<Icon sx={{ fontSize: 16 }} />}
                                                sx={{
                                                    borderRadius: '12px', px: 2.5, py: 1, textTransform: 'none', fontWeight: 600, fontSize: '.78rem',
                                                    background: alpha(B.purple, .1), color: B.light,
                                                    border: `1px solid ${alpha(B.purple, .2)}`,
                                                    '&:hover': { background: alpha(B.purple, .18) },
                                                }}>
                                            {btn.label}
                                        </Button>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Account details */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 1.75 }}>Account Details</Typography>
                            {[
                                { label: 'Account Name',   value: USER_NAME,    key: 'name' },
                                { label: 'Account Number', value: NG_ACCOUNT,   key: 'acct' },
                                { label: 'Bank',           value: NG_BANK,      key: 'bank' },
                                { label: 'NG Tag',         value: USER_TAG,     key: 'tag'  },
                            ].map((row, i, arr) => (
                                <React.Fragment key={row.key}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.1}>
                                        <Typography sx={{ color: textFaint, fontSize: '.78rem' }}>{row.label}</Typography>
                                        <Box display="flex" alignItems="center" gap={.75}>
                                            <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.82rem', letterSpacing: row.key === 'acct' ? '.08em' : 'normal' }}>
                                                {row.value}
                                            </Typography>
                                            <IconButton onClick={() => copy(row.value, row.key)} sx={{ p: .4, color: textFaint, '&:hover': { color: textPri } }}>
                                                {copiedField === row.key
                                                    ? <CheckOutlined sx={{ fontSize: 13, color: B.green }} />
                                                    : <ContentCopyOutlined sx={{ fontSize: 13 }} />
                                                }
                                            </IconButton>
                                        </Box>
                                    </Box>
                                    {i < arr.length - 1 && <Divider sx={{ borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}
                        </Box>

                        {/* Request specific amount */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 1.75 }}>
                                Request a Specific Amount
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <Typography sx={{ color: textMid, fontFamily: "'Instrument Serif', serif", fontSize: '1.4rem' }}>₦</Typography>
                                <input
                                    type="number" placeholder="0"
                                    value={reqAmount}
                                    onChange={e => setReqAmount(e.target.value)}
                                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: textPri, fontSize: '1.6rem', fontWeight: 700, fontFamily: "'Instrument Serif', serif" }}
                                />
                            </Box>
                            <Box sx={{ height: '1px', background: `linear-gradient(90deg, ${B.purple}, transparent)`, mb: 1.75 }} />
                            <input
                                placeholder="Add a note (optional)"
                                value={reqNote}
                                onChange={e => setReqNote(e.target.value)}
                                style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: textMid, fontSize: '.85rem', fontFamily: "'DM Sans', system-ui, sans-serif", marginBottom: '16px' }}
                            />
                            <Button fullWidth disabled={!numAmount}
                                    sx={{
                                        py: 1.4, borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '.85rem',
                                        background: `linear-gradient(135deg, ${B.purple}, ${B.light})`,
                                        color: '#fff', boxShadow: `0 8px 24px ${alpha(B.purple, .3)}`,
                                        '&:disabled': { opacity: .3, boxShadow: 'none' },
                                    }}>
                                {numAmount ? `Generate QR for ${fmt(numAmount)}` : 'Enter amount to generate QR'}
                            </Button>
                        </Box>

                        {/* Recent received */}
                        <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                            <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>Recently Received</Typography>
                            </Box>
                            {RECENT_RECEIVED.map((r, i) => (
                                <React.Fragment key={r.id}>
                                    <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <Typography sx={{ color: '#fff', fontSize: '.72rem', fontWeight: 700 }}>{r.avatar}</Typography>
                                        </Box>
                                        <Box flex={1}>
                                            <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.84rem' }}>{r.name}</Typography>
                                            <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>{r.time}</Typography>
                                        </Box>
                                        <Typography sx={{ color: B.green, fontWeight: 700, fontSize: '.86rem' }}>+{fmt(r.amount)}</Typography>
                                    </Box>
                                    {i < RECENT_RECEIVED.length - 1 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}
                        </Box>
                    </>
                )}

                {/* ══ SCAN TAB ══ */}
                {activeTab === 'scan' && (
                    <>
                        {/* Camera viewfinder mock */}
                        <Box sx={{
                            ...glassCard, overflow: 'hidden', mb: 2,
                            border: `1px solid ${alpha(B.purple, .25)}`,
                        }}>
                            {/* Viewfinder */}
                            <Box sx={{
                                position: 'relative', height: 280,
                                background: isDark ? alpha(B.dark, .9) : alpha(B.ink, .92),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                overflow: 'hidden',
                            }}>
                                {/* Scan area corners */}
                                {[
                                    { top: '50%', left: '50%', transform: 'translate(-90px, -90px)', borderTop: `3px solid ${B.purple}`, borderLeft: `3px solid ${B.purple}` },
                                    { top: '50%', left: '50%', transform: 'translate(66px, -90px)',  borderTop: `3px solid ${B.purple}`, borderRight: `3px solid ${B.purple}` },
                                    { top: '50%', left: '50%', transform: 'translate(-90px, 66px)',  borderBottom: `3px solid ${B.purple}`, borderLeft: `3px solid ${B.purple}` },
                                    { top: '50%', left: '50%', transform: 'translate(66px, 66px)',   borderBottom: `3px solid ${B.purple}`, borderRight: `3px solid ${B.purple}` },
                                ].map((corner, i) => (
                                    <Box key={i} sx={{ position: 'absolute', width: 24, height: 24, borderRadius: '2px', ...corner }} />
                                ))}

                                {/* Scan line animation */}
                                <Box sx={{
                                    position: 'absolute', left: '50%', transform: 'translateX(-90px)',
                                    width: 180, height: 2,
                                    background: `linear-gradient(90deg, transparent, ${B.purple}, ${B.light}, transparent)`,
                                    boxShadow: `0 0 8px ${alpha(B.purple, .6)}`,
                                    animation: 'scanLine 2s ease-in-out infinite',
                                    '@keyframes scanLine': {
                                        '0%':   { top: 'calc(50% - 80px)', opacity: 1 },
                                        '50%':  { top: 'calc(50% + 60px)', opacity: 1 },
                                        '100%': { top: 'calc(50% - 80px)', opacity: 1 },
                                    },
                                }} />

                                {/* Center QR icon */}
                                <Box sx={{ textAlign: 'center', zIndex: 2 }}>
                                    <QrCodeScannerOutlined sx={{ color: alpha('#fff', .2), fontSize: 64 }} />
                                </Box>

                                {/* Bottom label */}
                                <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, py: 1.5, background: `linear-gradient(0deg, ${alpha(B.dark, .9)} 0%, transparent 100%)`, textAlign: 'center' }}>
                                    <Typography sx={{ color: alpha('#fff', .5), fontSize: '.72rem', fontWeight: 600, letterSpacing: '.06em' }}>
                                        Point camera at QR code
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ p: 2.5 }}>
                                <Button fullWidth variant="contained"
                                        sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: '12px', py: 1.4, textTransform: 'none', fontWeight: 700, fontSize: '.9rem', boxShadow: `0 8px 24px ${alpha(B.purple, .35)}` }}>
                                    Open Camera
                                </Button>
                            </Box>
                        </Box>

                        {/* Or enter manually */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 1.75 }}>
                                Or enter account number
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: alpha(isDark ? '#fff' : B.dark, .04), border: `1px solid ${dividerC}`, borderRadius: '14px', px: 2, py: 1.25, mb: 1.5 }}>
                                <input
                                    placeholder="Enter 10-digit account number"
                                    style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: textPri, fontSize: '.9rem', fontFamily: "'DM Sans', system-ui, sans-serif" }}
                                />
                            </Box>
                            <Button fullWidth onClick={() => navigate('/transfer')}
                                    sx={{ py: 1.3, borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '.85rem', background: alpha(B.purple, .1), color: B.light, border: `1px solid ${alpha(B.purple, .2)}`, '&:hover': { background: alpha(B.purple, .18) } }}>
                                Proceed to Transfer
                            </Button>
                        </Box>

                        {/* Quick actions */}
                        <Box sx={{ ...glassCard, p: 2.5 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', fontWeight: 600, mb: 1.75 }}>Quick Actions</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
                                {[
                                    { label: 'Send Money',    icon: NorthEastOutlined, color: B.red,    path: '/transfer' },
                                    { label: 'Receive',       icon: SouthWestOutlined, color: B.green,  path: '/qr'       },
                                    { label: 'Pay Bills',     icon: SwapHorizOutlined, color: B.purple, path: '/bills'    },
                                ].map(action => {
                                    const Icon = action.icon;
                                    return (
                                        <Box key={action.label} onClick={() => navigate(action.path)} sx={{
                                            p: 1.75, borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                                            background: alpha(action.color, .08), border: `1px solid ${alpha(action.color, .18)}`,
                                            transition: 'all .18s',
                                            '&:hover': { transform: 'translateY(-2px)', background: alpha(action.color, .14) },
                                        }}>
                                            <Box sx={{ width: 38, height: 38, borderRadius: '11px', background: alpha(action.color, .12), display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
                                                <Icon sx={{ color: action.color, fontSize: 18 }} />
                                            </Box>
                                            <Typography sx={{ color: textMid, fontSize: '.68rem', fontWeight: 600, lineHeight: 1.2 }}>{action.label}</Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    </>
                )}

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default QRPage;